export default {
  id: 'what-are-scanners',
  title: 'What are Scanners?',
  content: `## What are Scanners?

Scanners are automated signal detectors that run across all active NSE stocks on a schedule. They look for specific technical conditions — breakouts, momentum shifts, candlestick patterns — and surface stocks that match.

Instead of manually checking 50+ stocks, scanners bring the setups to you.

---

## Where to Find Them

**Analytics tab** — the main scanner feed is shown here, updated throughout market hours.

The same scanner data also powers:
- ACT scenarios (scenarios are built from past scanner signals)
- Analysis Mode in Nyx AI (composite score includes scanner signal count)
- Algo Flows entry conditions (you can trigger a strategy when a scanner fires)

---

## How Scanners Run

| Scanner type | Runs every | Data used |
|---|---|---|
| Swing scanners | 30 minutes (9:15–15:30 IST) | Daily OHLCV candles + market features |
| Intraday (5-min) | 5 minutes (9:15–15:30 IST) | 5-minute intraday candles |
| Intraday (15-min) | 15 minutes (9:15–15:30 IST) | 15-minute intraday candles |

Results are stored with a timestamp. The analytics page always shows the latest run.

---

## What a Scanner Result Shows

Each result in the scanner feed displays:

| Field | Description |
|---|---|
| Symbol | NSE stock ticker |
| Signal | Scanner name that fired (e.g., BREAKOUT_20D_HIGH) |
| Current Price | Last fetched price |
| Volume Ratio | Current volume vs 20-day average (>2× is significant) |
| RSI | Current RSI(14) reading |
| Trend | UPTREND / DOWNTREND / SIDEWAYS vs SMA50 |
| Signal Strength | Composite strength score for this signal instance |
| Sector | Which Nifty sector the stock belongs to |

---

## Filters

You can filter scanner results by:
- Scanner category (Breakout, Momentum, Pattern, Volume, Intraday)
- RSI range (e.g., RSI < 40)
- Volume ratio minimum (e.g., > 1.5×)
- Sector
- Minimum confidence score

---

## Total Signal Coverage

TradeLab runs **27+ distinct scanner signals** across two categories:

- **Swing scanners** — Breakout signals, Pattern signals, Momentum signals, Price Action signals
- **Intraday scanners** — Opening Range, VWAP-based, Momentum, Gap, Squeeze signals

Each is described in detail in the Swing Scanners and Intraday Scanners topics.

---

## How to Use Scanner Results

A scanner result is a **starting point**, not a buy signal. It tells you a setup exists — your job is to evaluate whether the context supports a trade.

Steps when a scanner fires:
1. Check the trend — is price above or below SMA50?
2. Check RSI — is momentum extended or supportive?
3. Check volume — is there conviction behind the move?
4. Look at the chart — does the signal make sense visually?
5. Use Nyx AI Analysis Mode to see its explanation for the stock
6. If everything aligns, plan the trade in ACT or place it on the Trade page`,
};
